import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {createSelector} from "@reduxjs/toolkit";
import {SiteSiteAssoc, SiteSiteAssocEntity} from "../../../model/site-site-assoc";
import {adjustFromTimeZoneSafely} from "../../../date/date";

export interface SiteSiteAssocSelectorProps {
  sourceSiteId?: string
  effectiveOnly?: boolean
  mileageOnly?: boolean
}

const getSourceSiteIdParam = createParamSelector<SiteSiteAssocSelectorProps | undefined, string | undefined>(params => params?.sourceSiteId);
const getEffectiveOnlyParam = createParamSelector<SiteSiteAssocSelectorProps | undefined, boolean | undefined>(params => params?.effectiveOnly);
const getMileageOnlyParam = createParamSelector<SiteSiteAssocSelectorProps | undefined, boolean | undefined>(params => params?.mileageOnly);

export const siteSiteAssocsSelector = () => createSelector(
  [getUseQueryResultData<SiteSiteAssoc[]>, getSourceSiteIdParam, getEffectiveOnlyParam, getMileageOnlyParam],
  (data, sourceSiteId, effectiveOnly, mileageOnly) => {
    return selectSiteSiteAssocsAndTransform(
      data ?? [], {
        sourceSiteId: sourceSiteId,
        effectiveOnly: effectiveOnly,
        mileageOnly: mileageOnly
      }
    );
  }
);

export const siteSiteAssocSelector = () => createSelector(
  [getUseQueryResultData<SiteSiteAssoc>],
  (data) => {
    return data ? transformSiteSiteAssoc(data) : undefined;
  }
);

export const selectSiteSiteAssocs = (values?: SiteSiteAssoc[], selector?: SiteSiteAssocSelectorProps): SiteSiteAssoc[] => {
  if (!values) {
    return [];
  }

  const nowDttm = new Date().getTime()

  return values.filter((v) => {
    let filterIn = true;

    if (selector?.sourceSiteId) {
      if (v.sourceSiteId !== selector.sourceSiteId) {
        filterIn = false;
      }
    }

    if (selector?.effectiveOnly) {
      const adjustedStartDttm = adjustFromTimeZoneSafely(v.effectiveDttm)?.getTime()
      const adjustedEndDttm = adjustFromTimeZoneSafely(v.endDttm)?.getTime()
      const meetsStartThresh = adjustedStartDttm == null || adjustedStartDttm <= nowDttm
      const meetsEndThresh = adjustedEndDttm == null || adjustedEndDttm > nowDttm
      if (!meetsStartThresh || !meetsEndThresh) {
        filterIn = false
      }
    }

    if (selector?.mileageOnly) {
      if (typeof v.mileage === 'undefined') {
        filterIn = false
      }
    }

    return filterIn;
  });
};

export const transformSiteSiteAssoc = (value: SiteSiteAssoc): SiteSiteAssocEntity => {
  return SiteSiteAssocEntity.from(value)
}

export const selectSiteSiteAssocsAndTransform = (values?: SiteSiteAssoc[], selector?: SiteSiteAssocSelectorProps): SiteSiteAssocEntity[] => {
  const selected = selectSiteSiteAssocs(values, selector);
  return selected.map(transformSiteSiteAssoc);
};
